/**
 * Platform Entity
 *
 * Platforms with different surface types and physics properties.
 * Phase 3: Implement surface types (normal, sticky, slippery, bouncy, angled)
 * Phase 4.3.3 Revision: All platforms are rounded rectangles with reliable physics
 */

import Phaser from 'phaser';
import { GAME_CONSTANTS, SurfaceType } from '@/config/constants';
import { TYPOGRAPHY } from '@/config/designSystem';
import { PlatformRenderer } from '../rendering/PlatformRenderer';
import { PlatformPhysicsBuilder } from '../physics/PlatformPhysicsBuilder';
import { getZoneIdFromY } from '../level/ZoneThemes';
import { SettingsManager } from '../systems/SettingsManager';

export class Platform {
  private scene: Phaser.Scene;
  private body!: MatterJS.BodyType;
  private renderer: PlatformRenderer;
  private container: Phaser.GameObjects.Container;
  private zoneId: number;
  public surfaceType: SurfaceType;
  private debugLabel?: Phaser.GameObjects.Text;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    height: number,
    surfaceType: SurfaceType = SurfaceType.NORMAL,
    platformId: number = 0
  ) {
    this.scene = scene;
    this.surfaceType = surfaceType;

    // Determine zone from Y position
    // CRITICAL: Use correct world height (12725, not 11500) to convert Phaser Y to level Y
    this.zoneId = getZoneIdFromY(12725 - y); // Convert Phaser Y to level Y

    // Create container for graphics
    this.container = scene.add.container(x, y);
    this.container.setDepth(1); // Below Blobo (depth 10)

    // Create renderer for this platform
    this.renderer = new PlatformRenderer(scene, this.zoneId, surfaceType, platformId);

    // Render platform shapes (pass container for decorations)
    const graphics = this.renderer.render(0, 0, width, height, this.container);
    this.container.add(graphics);

    // DEBUG: Add platform ID label to identify platforms
    this.debugLabel = scene.add.text(0, -10, `#${platformId}`, {
      fontSize: '12px',
      color: '#ffff00',
      fontFamily: TYPOGRAPHY.fonts.josefin,
      stroke: '#000000',
      strokeThickness: 2,
    });
    this.debugLabel.setOrigin(0.5, 1); // Center above platform
    this.debugLabel.setVisible(SettingsManager.getShowPlatformIds()); // Respect setting
    this.container.add(this.debugLabel);

    // Setup physics (must match visual shape!)
    this.setupPhysicsBody(x, y, width, height);
  }

  private setupPhysicsBody(x: number, y: number, width: number, height: number): void {
    // All platforms use simple rectangle physics (matches visual rounded rectangle)
    this.body = PlatformPhysicsBuilder.createDefaultBody(this.scene, x, y, width, height);

    this.scene.matter.world.add(this.body);

    // CRITICAL: Set friction and restitution directly on the body
    this.body.friction = this.getSurfaceFriction();
    this.body.restitution = this.getSurfaceRestitution();
  }

  private getSurfaceFriction(): number {
    const surface = GAME_CONSTANTS.SURFACE[this.surfaceType];
    return surface?.FRICTION ?? 0.5;
  }

  private getSurfaceRestitution(): number {
    const surface = GAME_CONSTANTS.SURFACE[this.surfaceType];
    return surface?.RESTITUTION ?? 0.1;
  }

  /**
   * Set angle for angled platforms
   */
  public setAngle(angle: number): void {
    this.scene.matter.body.setAngle(this.body, Phaser.Math.DegToRad(angle));
    this.container.setAngle(angle);
  }

  /**
   * Get surface properties for display/debugging
   */
  public getSurfaceProperties(): {
    type: SurfaceType;
    friction: number;
    restitution: number;
    color: number;
  } {
    const surface = GAME_CONSTANTS.SURFACE[this.surfaceType];
    return {
      type: this.surfaceType,
      friction: this.getSurfaceFriction(),
      restitution: this.getSurfaceRestitution(),
      color: surface?.COLOR ?? 0x808080,
    };
  }

  /**
   * Get the physics body
   */
  public getBody(): MatterJS.BodyType {
    return this.body;
  }

  /**
   * Set visibility (v0.3.6 - viewport culling optimization)
   */
  public setVisible(visible: boolean): void {
    this.container.setVisible(visible);
  }

  /**
   * Enable/disable physics body (v0.8.4 - physics culling optimization)
   * When disabled, the body is removed from physics world to reduce collision checks
   */
  public setPhysicsActive(active: boolean): void {
    if (!this.body || !this.scene.matter?.world) return;

    // Only change if state is different to avoid unnecessary operations
    const isCurrentlyInWorld = this.scene.matter.world.has(this.body);

    if (active && !isCurrentlyInWorld) {
      // Add body back to physics world
      this.scene.matter.world.add(this.body);
    } else if (!active && isCurrentlyInWorld) {
      // Remove body from physics world (reduces collision detection overhead)
      this.scene.matter.world.remove(this.body);
    }
  }

  /**
   * Get bounds for culling calculations
   */
  public getBounds(): { y: number; height: number } {
    const bounds = this.body.bounds;
    return {
      y: this.body.position.y,
      height: bounds.max.y - bounds.min.y,
    };
  }

  /**
   * Update animations (call from GameScene update)
   */
  public update(deltaTime: number): void {
    this.renderer.updateAnimations(deltaTime);
  }

  /**
   * Get collectible decoration positions (for spawning collectibles)
   * Returns positions that were calculated during decoration phase
   */
  public getCollectiblePositions(): Array<{
    type: string;
    x: number;
    y: number;
    decorationIndex: number;
  }> {
    const positions = this.renderer.getDecorator().getCollectiblePositions();

    // Positions already contain local X/Y coordinates calculated during decoration
    // Just convert them to world coordinates
    const result: Array<{ type: string; x: number; y: number; decorationIndex: number }> = [];

    positions.forEach(pos => {
      // Convert local coordinates to world coordinates
      const worldX = this.body.position.x + pos.x;
      const worldY = this.body.position.y + pos.y;

      result.push({
        type: pos.type,
        x: worldX,
        y: worldY,
        decorationIndex: pos.decorationIndex,
      });
    });

    return result;
  }

  /**
   * Get platform ID (for collectible unique ID generation)
   */
  public getPlatformPosition(): { x: number; y: number } {
    return {
      x: this.body.position.x,
      y: this.body.position.y,
    };
  }

  /**
   * Clean up
   */
  public destroy(): void {
    // Check if Matter world still exists before trying to remove body
    if (this.scene.matter?.world) {
      this.scene.matter.world.remove(this.body);
    }
    this.renderer.destroy();
    this.container.destroy();
  }
}
